import catalogJson from '../data/automobile/catalog.json';
import type { AutomobileBrand, AutomobileCatalog } from '../types/automobile';
import { loadAutomobileLocalOverrides } from './automobileLocalOverrides';
import { dedupeAutomobileModels, getAutomobileModelDisplayName } from './automobileModelDisplay';

function dedupeCatalogModels(catalog: AutomobileCatalog): AutomobileCatalog {
  const brands = catalog.brands.map((b) => ({
    ...b,
    models: dedupeAutomobileModels(b.slug, b.models),
  }));
  const modelCount = brands.reduce((s, b) => s + b.models.length, 0);
  const brandsWithModels = brands.filter((b) => b.models.length > 0).length;
  return { ...catalog, brands, modelCount, brandsWithModels };
}

const rawCatalog = catalogJson as AutomobileCatalog;
/** Yüklemede de marka içi çift model adlarını temizle */
const baseCatalog = dedupeCatalogModels(rawCatalog);

function withLocalOverrides(catalog: AutomobileCatalog): AutomobileCatalog {
  const overrides = loadAutomobileLocalOverrides();
  const hasModel = Object.keys(overrides.modelImages).length > 0;
  const hasBrand = Object.keys(overrides.brandLogos).length > 0;
  const hasYears = Object.keys(overrides.modelYears || {}).length > 0;
  if (!hasModel && !hasBrand && !hasYears) return catalog;

  return {
    ...catalog,
    brands: catalog.brands.map((b) => {
      const logoOverride = overrides.brandLogos[b.slug];
      const models = b.models.map((m) => {
        const imgKey = `${b.slug}|${m.name}`;
        const imgOverride = overrides.modelImages[imgKey];
        const labelKey = `${b.slug}|${getAutomobileModelDisplayName(m)}`;
        const yearsOverride = overrides.modelYears?.[labelKey];

        if (!imgOverride && !yearsOverride) return m;
        return {
          ...m,
          imageUrl: imgOverride || m.imageUrl,
          imageGeneric: imgOverride ? false : m.imageGeneric,
          productionYears: yearsOverride || m.productionYears,
        };
      });
      return logoOverride ? { ...b, logoUrl: logoOverride, models } : { ...b, models };
    }),
  };
}

export function getAutomobileCatalog(): AutomobileCatalog {
  return withLocalOverrides(baseCatalog);
}

export function getAutomobileBrand(slug: string): AutomobileBrand | undefined {
  return getAutomobileCatalog().brands.find((b) => b.slug === slug);
}

export function getAutomobileBrandSlugs(): string[] {
  return getAutomobileCatalog().brands.map((b) => b.slug);
}

/** Build sırasında kullanılan ham katalog (override yok) */
export function getAutomobileCatalogBase(): AutomobileCatalog {
  return baseCatalog;
}

/** Supabase birleştirmede yalnızca marka meta (açıklama vb.); model listesi eklenmez */
export function getAutomobileCatalogBrandMetadata(): AutomobileCatalog {
  return {
    ...baseCatalog,
    modelCount: 0,
    brandsWithModels: 0,
    brands: baseCatalog.brands.map((b) => ({ ...b, models: [] })),
  };
}
